import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const SEO = ({ titleKey, descriptionKey, title, description, image, type = 'website', url, noindex = false }) => {
  const { t, language } = useLanguage();
  const location = useLocation();

  const pageTitle = title || (titleKey ? t(titleKey) : '');
  const pageDescription = description || (descriptionKey ? t(descriptionKey) : '');
  const siteName = 'CasaOthers';
  const fullTitle = pageTitle ? `${pageTitle} | ${siteName}` : siteName;
  const ogImage = image || 'https://images.unsplash.com/photo-1743112412074-107329677542?crop=entropy&cs=srgb&fm=jpg&q=85';

  const origin = typeof window !== 'undefined' && window.location ? window.location.origin : '';
  /* Canonical: prefer explicit `url` prop (used by BlogDetail to point at
     https://casaothers.nl). If the site is running on the canonical
     production host, self-refer. Otherwise (preview/staging), still emit
     a canonical pointing at the production URL so search engines
     consolidate signal on casaothers.nl and don't index preview URLs. */
  const CANONICAL_HOST = 'https://casaothers.nl';
  const ALT_HOST = 'https://casaothers.pl'; // Polish sister site — separate deployment, mirrored routes
  const isCanonicalHost = origin && origin.replace(/\/$/, '') === CANONICAL_HOST;
  const canonical = url || `${isCanonicalHost ? origin : CANONICAL_HOST}${location.pathname}`;
  /* hreflang alternates. The Polish sister site (casaothers.pl) runs on
     its own Emergent deployment but mirrors every route slug + property
     ID 1-on-1, so we can derive the alt URL directly from the current
     pathname. `x-default` points at .nl because that's the primary site
     and the fallback for non-Polish visitors. */
  const nlAlternate = `${CANONICAL_HOST}${location.pathname}`;
  const plAlternate = `${ALT_HOST}${location.pathname}`;

  return (
    <Helmet>
      <html lang={language} />
      <title>{fullTitle}</title>
      <meta name="description" content={pageDescription} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {process.env.REACT_APP_GOOGLE_SITE_VERIFICATION && (
        <meta name="google-site-verification" content={process.env.REACT_APP_GOOGLE_SITE_VERIFICATION} />
      )}
      <link rel="canonical" href={canonical} />
      {/* hreflang — Dutch stays on casaothers.nl, Polish visitors get
          routed to the casaothers.pl sister deployment (same paths).
          Skipped entirely for noindex pages: hreflang on a noindex URL
          confuses Google and can leak the URL into the search index. */}
      {!noindex && <link rel="alternate" hrefLang="nl" href={nlAlternate} />}
      {!noindex && <link rel="alternate" hrefLang="pl" href={plAlternate} />}
      {!noindex && <link rel="alternate" hrefLang="x-default" href={nlAlternate} />}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={pageTitle || siteName} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={language === 'nl' ? 'nl_NL' : 'en_US'} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={pageTitle || siteName} />
    </Helmet>
  );
};

export default SEO;
